import { Injectable, signal } from '@angular/core';

/** `auto` sigue la preferencia del sistema operativo (y reacciona si cambia mientras se usa la app). */
export type ThemeMode = 'light' | 'dark' | 'auto';

const MODE_KEY = 'wm-theme-mode';
/** Clave anterior (solo `dark`/`light`): se sigue leyendo para no perder la elección de quien ya la había hecho. */
const LEGACY_KEY = 'wm-theme';

const isMode = (value: string | null): value is ThemeMode => value === 'light' || value === 'dark' || value === 'auto';

@Injectable({ providedIn: 'root' })
export class DarkModeService {
  /** Lo que eligió la persona (claro, oscuro o automático). */
  readonly mode = signal<ThemeMode>('auto');
  /** Lo que se está pintando ahora (resuelve `auto` con el sistema). */
  readonly isDark = signal(false);

  private readonly system: MediaQueryList | null;

  constructor() {
    if (typeof window === 'undefined') {
      this.system = null;
      return;
    }

    this.system = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    this.system?.addEventListener?.('change', () => {
      if (this.mode() === 'auto') this.refresh();
    });

    this.mode.set(this.storedMode());
    this.refresh();
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
    localStorage.setItem(MODE_KEY, mode);
    this.refresh();
  }

  /** Alterna claro/oscuro con un botón (queda como elección explícita, ya no automática). */
  toggle(): void {
    this.setMode(this.isDark() ? 'light' : 'dark');
  }

  private storedMode(): ThemeMode {
    const saved = localStorage.getItem(MODE_KEY);
    if (isMode(saved)) return saved;
    const legacy = localStorage.getItem(LEGACY_KEY);
    return legacy === 'dark' || legacy === 'light' ? legacy : 'auto';
  }

  private refresh(): void {
    const dark = this.mode() === 'dark' || (this.mode() === 'auto' && !!this.system?.matches);
    this.isDark.set(dark);
    document.documentElement.classList.toggle('dark', dark);
  }
}
